import React, {
	useCallback,
	useMemo,
	useState,
} from 'react';
import {
	Flex,
	Typography,
} from 'antd';
import {
	EstimateStatus,
	EstimatorModeTypes,
	SeaPassageToggles,
} from '@shared/utils/constants';
import { SeaPassageSettings } from '@api/utils/sequelize/calculateVoyageItineraryDetails/helpers/itineraryConstants';
import Modal from '@client/components/Modal/Modal';
import EditableCellTableRedux from '@client/components/EditableTableRedux/EditableCellTableRedux';
import EditableCellRedux from '@client/components/EditableTableRedux/EditableCellRedux';
import { useNavigationBlock } from '@client/lib/navigationBlock';
import { useSaveButton } from '@client/utils/hooks/useSaveButton';
import { useSpreadsheetNavigation } from '@client/utils/hooks/useSpreadsheetNavigation';
import SpreadsheetNavigationHint from '@client/components/SpreadsheetNavigationHint';
import { usePortRotationProvider } from '../context/PortRotationContext';
import { useCargoProvider } from '../context/CargoContext';
import { useEstimate } from '../context/EstimatorContext';
import { useEstimateChangesProvider } from '../context/EstimateChangesContext';
import { useCentralStore } from '../context/CentralStoreContext';
import { LocalPortRotation } from '../context/hooks/usePortRotationHandlers';
import { getPortsModalColumns } from '../helpers/getPortRotationColumns';
import { hasCargoQtyMismatch } from '../helpers/hasCargoQtyMismatch';
import styles from '../styles/shared.module.css';
import modalStyles from './styles/PortsModal.module.css';

const PortsModal = ({ onClose }: { onClose: () => void }) => {
	const {
		portRotation,
		onPortRotationChange,
		onPortRotationRowChange,
		onConfigUpdate,
		ports,
		consumptionSets,
		defaultConsumptionSetId,
		setSyncPortRotation,
	} = usePortRotationProvider();

	const [speedPopoverOpen, setSpeedPopoverOpen] = useState<number | null>(null);

	const {
		cargos,
		hasCargoRelet,
	} = useCargoProvider();

	const { estimate, onUpdateEstimate } = useEstimate();
	const { refreshEverything } = useCentralStore();
	const { makeBlockable } = useNavigationBlock();
	const navigationProps = useSpreadsheetNavigation();

	const {
		saving,
		onSaveChanges,
		hasPendingChanges,
		onClearPendingChanges,
		immediateSaveChanges,
	} = useEstimateChangesProvider();

	const saveButton = useSaveButton({
		title: 'Save & Calculate',
		onSave: onSaveChanges,
		hasPendingChanges,
		loading: saving,
	});

	const handlePortRotationConfigSave = useCallback((id: number, settings: SeaPassageSettings) => {
		immediateSaveChanges(() => {
			onConfigUpdate(id, settings);
			setSyncPortRotation('full');
		});
	}, [immediateSaveChanges, onConfigUpdate, setSyncPortRotation]);

	const handleSeaPassageTogglesSave = useCallback((toggles: Partial<SeaPassageToggles>) => {
		immediateSaveChanges(() => {
			onUpdateEstimate({ routeOptions: toggles });
			portRotation.forEach((entry) => onConfigUpdate(entry.id, { ...entry.routeOptions, ...toggles }));
			setSyncPortRotation('full');
		});
	}, [immediateSaveChanges, onUpdateEstimate, portRotation, onConfigUpdate, setSyncPortRotation]);

	const handleRowChange = useCallback((row: LocalPortRotation) => {
		onPortRotationRowChange(row.id, row);
	}, [onPortRotationRowChange]);

	const columns = useMemo(() => getPortsModalColumns({
		showQtyError: hasCargoQtyMismatch(cargos, portRotation),
		onPortRotationRowChange,
		onPortRotationChange,
		handlePortRotationConfigSave,
		handleSeaPassageTogglesSave,
		currency: estimate?.currency ?? 'USD',
		ports: ports ?? [],
		mode: EstimatorModeTypes.GRID,
		cargos,
		disabled: estimate?.status === EstimateStatus.Fixed,
		waitForLaycan: estimate?.waitForLaycan,
		hasCargoRelet,
		portRotation,
		consumptionSets,
		defaultConsumptionSetId,
		speedPopoverOpen,
		setSpeedPopoverOpen,
	}), [
		cargos,
		portRotation,
		onPortRotationRowChange,
		onPortRotationChange,
		consumptionSets,
		defaultConsumptionSetId,
		speedPopoverOpen,
		handlePortRotationConfigSave,
		handleSeaPassageTogglesSave,
		estimate?.currency,
		estimate?.status,
		estimate?.waitForLaycan,
		ports,
		hasCargoRelet,
	]);

	const onCancel = makeBlockable(async () => {
		onClose();

		if (hasPendingChanges) {
			onClearPendingChanges();
			await refreshEverything();
		}
	});

	return (
		<Modal
			open
			title={(
				<Flex align="center" justify="space-between" className={modalStyles.title}>
					<Typography.Title level={4}>
						{`Port rotation${estimate?.name ? ` - ${estimate.name}` : ''}`}
					</Typography.Title>
					<Flex align="center" gap={16}>
						<SpreadsheetNavigationHint />
						{saveButton}
					</Flex>
				</Flex>
			)}
			onCancel={onCancel}
			footer={null}
			width="90%"
			destroyOnClose
		>
			<div {...navigationProps}>
				<EditableCellTableRedux
					components={{
						body: {
							cell: EditableCellRedux,
						},
					}}
					size="small"
					pagination={false}
					bordered
					scroll={{ x: 1200 }}
					rowKey="key"
					columns={columns}
					dataSource={portRotation}
					onRowChange={handleRowChange}
					onChange={onPortRotationChange}
					loading={false}
					className={styles.portRotationTable}
				/>
			</div>
		</Modal>
	);
};

export default PortsModal;
